import axios from "axios";
import { redirectToShopifyInstall } from "./embeddedAppUtils";

/**
 * Creates a configured axios instance for all API requests throughout the app.
 * Authentication is entirely session-token based (see request interceptor below) --
 * there is no more localStorage-cached JWT/refresh-token flow.
 */
const axiosInstance = axios.create();

/**
 * Fetches a fresh Shopify App Bridge session token.
 * window.shopify.idToken() is provided by the App Bridge CDN script loaded in
 * public/index.html. Session tokens are short-lived (about a minute), so we always
 * fetch a new one rather than caching it ourselves -- App Bridge already does the
 * necessary caching/refreshing internally.
 *
 * Example: const token = await getShopifySessionToken()
 *   => "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
 */
export async function getShopifySessionToken() {
  const sessionToken = await window.shopify?.idToken();
  if (!sessionToken) {
    throw new Error("Shopify session token unavailable");
  }
  return sessionToken;
}

/**
 * Alias used by chat fetch() calls that previously asked for a JWT access token.
 * Now returns the same App Bridge session token the axios interceptor attaches.
 */
export async function ensureValidAccessToken() {
  return getShopifySessionToken();
}

/**
 * Request interceptor: attaches a fresh Shopify App Bridge session token to every
 * outgoing request, plus X-Debug-Shop when a SUPER_ADMIN is impersonating.
 *
 * Pass `{ optionalAuth: true }` on a request to still send it when no session
 * token is available (used by public endpoints such as GET /api/billingplan).
 *
 * Example: axiosInstance.get(url, { optionalAuth: true })
 */
axiosInstance.interceptors.request.use(async (req) => {
  try {
    const sessionToken = await getShopifySessionToken();
    req.headers.Authorization = `Bearer ${sessionToken}`;
    const debugShop = sessionStorage.getItem("debugShop");
    if (debugShop) {
      req.headers["X-Debug-Shop"] = debugShop;
    }
    return req;
  } catch (error) {
    if (req.optionalAuth) {
      return req;
    }
    console.error("Unable to fetch Shopify session token:", error);
    return Promise.reject(error);
  }
});

/**
 * Response interceptor: watches for the backend's re-authorization signal.
 *
 * shopify_reauth_required (set on the ResponseMapper's `data` payload, alongside a 401
 * status) means the shop's permanent Shopify token is no longer valid -- the shop
 * either uninstalled the app or Shopify otherwise revoked access. There is nothing the
 * frontend can retry here; the merchant needs to go through OAuth again.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const responseBody = error.response?.data;
    if (error.response?.status === 401 && responseBody?.data?.shopify_reauth_required) {
      redirectToShopifyInstall();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
